import { Alert, Button, Code, CopyButton, Group, List, PinInput, SimpleGrid, Stack, Text } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiAlertCircle, FiCheck, FiCopy } from 'react-icons/fi';
import { errorMessage } from '@web/lib/errors';
import { useMfaEnable, useMfaSetup } from './api';

/**
 * Two-factor enrolment: scan the QR code, confirm with a code, then save the recovery codes.
 * `onDone` fires only after the user confirms they saved the codes.
 */
export function MfaEnroll({ onDone, submitType = 'button' }: { onDone: () => void; submitType?: 'button' | 'submit' }) {
  const { t } = useTranslation();
  const setup = useMfaSetup();
  const enable = useMfaEnable();
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  const started = useRef(false);
  useEffect(() => {
    // Exactly one setup request: a second one would replace the secret behind the QR code.
    if (started.current) return;
    started.current = true;
    setup.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (recoveryCodes) {
    return (
      <Stack gap="md">
        <Alert color="green" icon={<FiCheck />} title={t('auth.mfa.enabledTitle')}>
          {t('auth.mfa.recoveryIntro')}
        </Alert>
        <SimpleGrid cols={2} spacing={6} data-testid="recovery-codes">
          {recoveryCodes.map((c) => (
            <Code key={c} fz="sm" ta="center" py={6}>
              {c}
            </Code>
          ))}
        </SimpleGrid>
        <Group justify="space-between">
          <CopyButton value={recoveryCodes.join('\n')}>
            {({ copied, copy }) => (
              <Button variant="default" leftSection={copied ? <FiCheck /> : <FiCopy />} onClick={copy}>
                {copied ? t('common.copied') : t('common.copy')}
              </Button>
            )}
          </CopyButton>
          <Button type={submitType} onClick={onDone}>
            {t('auth.mfa.saved')}
          </Button>
        </Group>
      </Stack>
    );
  }

  if (setup.isError) {
    return (
      <Alert color="red" icon={<FiAlertCircle />}>
        {errorMessage(setup.error)}
      </Alert>
    );
  }
  if (!setup.data) return <Text c="dimmed">{t('common.loading')}</Text>;

  const confirm = (value?: string) =>
    enable.mutate(value ?? code, { onSuccess: (res) => setRecoveryCodes(res.recoveryCodes), onError: () => setCode('') });

  return (
    <Stack gap="md">
      <List type="ordered" size="sm" spacing={4}>
        <List.Item>{t('auth.mfa.step1')}</List.Item>
        <List.Item>{t('auth.mfa.step2')}</List.Item>
        <List.Item>{t('auth.mfa.step3')}</List.Item>
      </List>
      <Group justify="center">
        <img src={setup.data.qrDataUrl} alt={t('auth.mfa.qrAlt')} width={180} height={180} />
      </Group>
      <Text size="xs" c="dimmed" ta="center">
        {t('auth.mfa.manual')}{' '}
        <Code data-testid="mfa-secret">{setup.data.secret}</Code>
      </Text>
      {enable.isError && (
        <Alert color="red" icon={<FiAlertCircle />}>
          {errorMessage(enable.error)}
        </Alert>
      )}
      <Group justify="center">
        <PinInput length={6} type="number" oneTimeCode value={code} onChange={setCode} onComplete={confirm} aria-label={t('auth.mfa.codeLabel')} />
      </Group>
      <Button type={submitType} fullWidth loading={enable.isPending} disabled={code.length !== 6} onClick={() => confirm()}>
        {t('auth.mfa.enable')}
      </Button>
    </Stack>
  );
}
